/**
 * SchemaRegistry: register schemas, synchronize, migrate.
 */

import { db } from './connection';
import { EntityManager } from './entity-manager';
import type { EntitySchema, MigrationAction } from './types';

interface SchemaVersion {
  table_name: string;
  version: number;
  migrated_at: string;
}

class SchemaRegistry {
  private _schemas = new Map<string, EntitySchema<Record<string, unknown>>>();
  private _managers = new Map<string, EntityManager<Record<string, unknown>>>();

  register<T extends Record<string, unknown>>(
    schema: EntitySchema<T>
  ): EntityManager<T> {
    this._schemas.set(schema.tableName, schema as EntitySchema<Record<string, unknown>>);
    const manager = new EntityManager<T>(schema);
    this._managers.set(schema.tableName, manager as EntityManager<Record<string, unknown>>);
    return manager;
  }

  get<T extends Record<string, unknown>>(tableName: string): EntityManager<T> {
    const manager = this._managers.get(tableName);
    if (!manager) {
      throw new Error(`Entity manager for "${tableName}" not found. Did you register it?`);
    }
    return manager as EntityManager<T>;
  }

  getSchema<T extends Record<string, unknown>>(
    tableName: string
  ): EntitySchema<T> {
    const schema = this._schemas.get(tableName);
    if (!schema) {
      throw new Error(`Schema for "${tableName}" not found.`);
    }
    return schema as EntitySchema<T>;
  }

  async synchronize(): Promise<void> {
    await db.execute(`
      CREATE TABLE IF NOT EXISTS _schema_versions (
        table_name TEXT PRIMARY KEY,
        version INTEGER NOT NULL,
        migrated_at TEXT NOT NULL
      )
    `);

    for (const [tableName, manager] of Array.from(this._managers)) {
      const exists = await manager.tableExists();
      if (!exists) {
        await manager.createTable();
        await this._setSchemaVersion(tableName, 1);
      }
    }
  }

  async migrate(
    tableName: string,
    version: number,
    actions: MigrationAction[]
  ): Promise<void> {
    const currentVersion = await this._getSchemaVersion(tableName);
    if (currentVersion >= version) return;

    const manager = this.get(tableName);

    await db.transaction(async () => {
      for (const action of actions) {
        await manager.alterTable(action);
      }
      await this._setSchemaVersion(tableName, version);
    });
  }

  async dropAll(): Promise<void> {
    for (const manager of Array.from(this._managers.values())) {
      try {
        await manager.dropTable();
      } catch {
        // Table might not exist
      }
    }
    await db.execute('DROP TABLE IF EXISTS _schema_versions');
  }

  private async _getSchemaVersion(tableName: string): Promise<number> {
    const result = await db.query<SchemaVersion>(
      'SELECT version FROM _schema_versions WHERE table_name = ?',
      [tableName]
    );
    return result[0]?.version ?? 0;
  }

  private async _setSchemaVersion(tableName: string, version: number): Promise<void> {
    await db.run(
      `INSERT OR REPLACE INTO _schema_versions (table_name, version, migrated_at)
       VALUES (?, ?, ?)`,
      [tableName, version, new Date().toISOString()]
    );
  }
}

export const schemaRegistry = new SchemaRegistry();
